import React, { FC, useMemo, useState } from "react";
import { Tooltip } from "antd";
import { CheckOutlined, PlayCircleFilled } from "@ant-design/icons";
import moment from "moment";
import { AlertSeverity, BeamAlert } from "./types";
import BeamIcon, { BeamIconName } from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * Alerts message type — a severity-driven feed.
 *
 * Severity is carried by a left border accent rather than a full-bleed
 * background: a stack of critical alerts stays readable, and the accent still
 * reads at a glance when scrolling fast.
 */

interface AlertsCardProps {
  alerts: BeamAlert[];
  onAcknowledge?: (alert: BeamAlert) => void;
  onDispatch?: (alert: BeamAlert) => void;
  onViewLog?: (alert: BeamAlert) => void;
}

const SEVERITY_META: Record<
  AlertSeverity,
  { label: string; color: string; icon: BeamIconName }
> = {
  critical: { label: "Critical", color: "#FF5F25", icon: "emergency" },
  warning: { label: "Warning", color: "#FBAE16", icon: "warning" },
  info: { label: "Info", color: "#508afa", icon: "alert" },
};

/**
 * Maps the alert's type onto the app's own artwork — every one of these
 * already exists in public/images, so no generic stand-in is needed.
 */
const typeIcon = (type: string, severity: AlertSeverity): BeamIconName => {
  const key = type.toLowerCase();
  if (key.includes("speed")) return "car";
  if (key.includes("vital") || key.includes("hr")) return "heart";
  if (key.includes("geofence")) return "area";
  if (key.includes("capture")) return "camera";
  return SEVERITY_META[severity].icon;
};

const FILTERS: (AlertSeverity | "all")[] = ["all", "critical", "warning", "info"];

const AlertsCard: FC<AlertsCardProps> = ({
  alerts,
  onAcknowledge,
  onDispatch,
  onViewLog,
}) => {
  const [filter, setFilter] = useState<AlertSeverity | "all">("all");
  const [acknowledged, setAcknowledged] = useState<string[]>([]);

  const counts = useMemo(
    () =>
      alerts.reduce<Record<string, number>>(
        (acc, alert) => {
          acc[alert.severity] = (acc[alert.severity] || 0) + 1;
          return acc;
        },
        { all: alerts.length }
      ),
    [alerts]
  );

  const visible =
    filter === "all" ? alerts : alerts.filter((a) => a.severity === filter);

  const acknowledge = (alert: BeamAlert) => {
    setAcknowledged((prev) => [...prev, alert.id]);
    onAcknowledge?.(alert);
  };

  return (
    <section className="mt-card mt-alerts">
      <header className="mt-card__head">
        <h4>Alerts</h4>
        <div className="mt-segmented">
          {FILTERS.map((key) => (
            <button
              key={key}
              type="button"
              className={filter === key ? "is-active" : ""}
              onClick={() => setFilter(key)}
            >
              {key === "all" ? "All" : SEVERITY_META[key].label}
              <span className="mt-segmented__count">{counts[key] || 0}</span>
            </button>
          ))}
        </div>
      </header>

      <div className="mt-card__body mt-card__body--flush">
        {visible.map((alert) => {
          const meta = SEVERITY_META[alert.severity];
          const isAcked = acknowledged.includes(alert.id);

          return (
            <article
              key={alert.id}
              className={`mt-alert mt-alert--${alert.severity} ${
                isAcked ? "is-acked" : ""
              }`}
            >
              <div className="mt-alert__head">
                <span
                  className="mt-alert__icon"
                  style={{ color: meta.color }}
                >
                  <BeamIcon name={typeIcon(alert.type, alert.severity)} size={16} />
                </span>
                <span className="mt-alert__type">{alert.type}</span>
                <span
                  className="mt-alert__severity"
                  style={{ color: meta.color, borderColor: meta.color }}
                >
                  {meta.label}
                </span>
                <Tooltip
                  title={moment(alert.occurredAt).format("D MMM YYYY, h:mm:ss A")}
                >
                  <span className="mt-alert__time">
                    {moment(alert.occurredAt).fromNow()}
                  </span>
                </Tooltip>
              </div>

              <div className="mt-alert__body">
                <p className="mt-alert__desc">{alert.description}</p>
                {alert.artifact && (
                  <button
                    type="button"
                    className="mt-alert__artifact"
                    onClick={() => onViewLog?.(alert)}
                  >
                    <img src={alert.artifact.url} alt="" />
                    {alert.artifact.kind === "video" && (
                      <PlayCircleFilled className="mt-alert__artifact-play" />
                    )}
                  </button>
                )}
              </div>

              <div className="mt-alert__actions">
                <button
                  type="button"
                  className="mt-btn mt-btn--ghost"
                  disabled={isAcked}
                  onClick={() => acknowledge(alert)}
                >
                  <CheckOutlined /> {isAcked ? "Acknowledged" : "Acknowledge"}
                </button>
                <button
                  type="button"
                  className="mt-btn mt-btn--primary"
                  style={{ background: meta.color, borderColor: meta.color }}
                  onClick={() => onDispatch?.(alert)}
                >
                  <BeamIcon name="send" size={14} /> Dispatch Response
                </button>
                <button
                  type="button"
                  className="mt-btn mt-btn--link"
                  onClick={() => onViewLog?.(alert)}
                >
                  <BeamIcon name="doc" size={14} /> View Full Log
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default AlertsCard;
